import { ProjectCharge } from "~/gql/types";
import { ChargeFieldState, FormStateDependencies } from "../types";
import { FieldName, isLiveField } from "./liveFields";

export type FieldStates = Record<FieldName, ChargeFieldState>;

export type DataDependentStates = Pick<
  FieldStates,
  "rate" | "customRateLabel" | "unit" | "rateAmount"
>;

/**
 * Per-field `{ visible, disabled, required }` for the edit form, composed as
 * `C ∘ authDependentState ∘ dataDependentState` from three deliberately distinct
 * layers. They are kept separate — not fused into one per-field boolean — because
 * dataDependentState and authDependentState are durable rules with independent
 * lifetimes, while C is a temporary migration scaffold that must be cleanly
 * deletable when the old charge form is retired.
 *
 *   dataDependentState — data prerequisites (durable). "Can't fill this until
 *       its inputs are filled." Keyed on other form values (e.g. the override
 *       toggle hides `rate` vs `customRateLabel`). Lives in
 *       `deriveDataDependentStates` below.
 *
 *   authDependentState — operation / auth / lifecycle (durable). "Shown/hidden
 *       by operation, role, or charge state." Keyed on the server capability
 *       flags carried on the charge (`canEditDate`, `canMoveProject`,
 *       `canEditTaxable`, ...), which are opaque inputs — the form enforces and
 *       reports them, it does not compute them. Lives in
 *       `deriveCapabilityStates` below.
 *
 *   C — migration pass-through (TEMPORARY). "Not ported to the new form yet →
 *       lock it and carry its loaded value through unchanged." Keyed on the
 *       `liveFields` manifest, the single source of truth for what the new form
 *       owns.
 *
 * C is an outer, deletable wrapper over the durable pair. It is strictly
 * subordinate to authDependentState on visibility: it may only ever ADD a lock,
 * never make a field visible that authDependentState hides (otherwise the new
 * form would show a disabled field
 * where the old form correctly shows nothing — a regression the round-trip test
 * cannot catch, since the value still passes through). It also never makes an
 * un-owned field user-required. When the old form is retired, delete layer C and
 * the `liveFields` manifest; dataDependentState and authDependentState are
 * untouched.
 *
 * The composition rules:
 *
 *   visible  = authDependentState.visible                 // op/capability alone decides visibility
 *   disabled = authDependentState.disabled || dataDependentState.disabled || (field ∉ liveFields)  // any layer can ADD a lock
 *   required = field ∈ liveFields ? dataDependentState.required || authDependentState.required : false  // un-owned fields are never user-required
 *
 * Today the live fields' dataDependentState/authDependentState rules are
 * near-trivial (the current cluster is always editable on edit and mostly
 * ungated); the layering is fixed now so future clusters slot durable behavior
 * into dataDependentState/authDependentState without disturbing C.
 */
export const deriveFieldStates = (
  values: FormStateDependencies,
  charge: ProjectCharge,
  showProjectField = true
): FieldStates => {
  /**
   * Data prerequisites (durable). Currently only rate/customRateLabel/
   * unit have non-trivial prerequisites; every other field defaults to enabled.
   */
  const dataDependentState = deriveDataDependentStates(values);

  /**
   * Operation / auth / lifecycle (durable). For the edit purpose the
   * charge's capability flags (canXXX from the server-returned data) gate visibility;
   * the current live fields are ungated so most fields are simply visible. The shape
   * is fixed now so later clusters slot in.
   */
  const authDependentState = deriveCapabilityStates(charge);

  if (!showProjectField) {
    authDependentState.project = {
      ...authDependentState.project,
      visible: false,
    };
  }

  const hasDataDependentState = (
    field: FieldName
  ): field is keyof DataDependentStates => field in dataDependentState;

  const compose = (field: FieldName): ChargeFieldState => {
    const auth = authDependentState[field];
    // dataDependentState only defines a handful of fields (rate/customRateLabel/unit/rateAmount).
    // Where it does, it owns their data-prerequisite visibility (e.g. the
    // override toggle hides `rate` vs `customRateLabel`); intersect it with auth.
    const data = hasDataDependentState(field)
      ? dataDependentState[field]
      : undefined;

    return {
      visible: auth.visible && (data?.visible ?? true),
      disabled: auth.disabled || (data?.disabled ?? false),
      required: (data?.required ?? false) || auth.required,
    };
  };

  const allFields: FieldName[] = [
    "rate",
    "rateAmount",
    "unit",
    "date",
    "notes",
    "billable",
    "type",
    "customRateLabel",
    "useCustomRate",
    "eeCode",
    "quantity",
    "rates",
    "editNotes",
    "employee",
    "project",
    "taxable",
    "rateSheetName",
    "label",
  ];

  return allFields.reduce((acc, field) => {
    const composed = compose(field);
    acc[field] = {
      ...composed,
      disabled: composed.disabled || !isLiveField(field),
      required: isLiveField(field) ? composed.required : false,
    };
    return acc;
  }, {} as FieldStates);
};

/**
 * Data-dependent state: prerequisites keyed purely on other form values. Unlike
 * `deriveCapabilityStates`, it does not read the charge's capability flags.
 */
export const deriveDataDependentStates = (
  values: FormStateDependencies
): DataDependentStates => {
  const { useCustomRate, date, project, rateSheetName } = values;

  const overrideRate = useCustomRate === true;
  const unitFieldDisabled = !date || !project || !overrideRate;
  const rateFieldDisabled = !date || !project;
  const rateAmountDisabled = !rateSheetName && !overrideRate;

  return {
    rate: {
      required: true,
      disabled: rateFieldDisabled,
      visible: !overrideRate,
    },
    customRateLabel: {
      required: true,
      visible: overrideRate,
      disabled: false,
    },
    unit: {
      required: true,
      disabled: unitFieldDisabled,
      visible: true,
    },
    rateAmount: {
      required: true,
      disabled: rateAmountDisabled,
      visible: true,
    },
  };
};

/**
 * Auth-dependent state for the edit purpose. Visibility follows the same
 * charge-capability gates the old form applies (`canEditDate`,
 * `canMoveProject`, `canEditTaxable`); everything else is visible.
 * Disabled is `false` for every field — the current live fields are always
 * editable on edit — with the migration pass-through adding the lock on top.
 * Required is `false` except where a field is required whenever shown:
 * `editNotes` always, `employee` unless the charge is third-party, and
 * `eeCode` when `canEditTaxable`.
 */
const deriveCapabilityStates = (charge: ProjectCharge): FieldStates => {
  const visible = (v: boolean): ChargeFieldState => ({
    visible: v,
    disabled: false,
    required: false,
  });

  const shown = visible(true);

  return {
    rate: shown,
    rateAmount: shown,
    unit: shown,
    date: visible(charge.canEditDate),
    notes: shown,
    billable: shown,
    type: visible(false),
    customRateLabel: shown,
    useCustomRate: shown,
    eeCode: {
      visible: charge.canEditTaxable,
      disabled: false,
      required: charge.canEditTaxable,
    },
    quantity: shown,
    rates: shown,
    editNotes: { visible: true, disabled: false, required: true },
    employee: {
      visible: !charge.isThirdParty,
      disabled: false,
      required: !charge.isThirdParty,
    },
    project: visible(charge.canMoveProject),
    taxable: visible(charge.canEditTaxable),
    rateSheetName: shown,
    label: shown,
  };
};
